<template>
  <div class="fluxo-vendas-page">
    <!-- Container 1: Filtros -->
    <pulse-card
      title="Filtros"
      subtitle="Defina a empresa e o período para carregar os contratos."
    >
      <template #actions>
        <pulse-button
          :label="mostrarFiltro ? 'Ocultar filtro' : 'Mostrar filtro'"
          :icon="mostrarFiltro ? 'pi pi-eye-slash' : 'pi pi-eye'"
          severity="secondary"
          outlined
          @click="alternarVisibilidade"
        />
      </template>

      <div v-if="mostrarFiltro" class="filtros-grid">
        <pulse-field label="Empresa">
          <pulse-select
            v-model="selectedCompany"
            :items="companyOptions"
            item-text="text"
            item-value="value"
            placeholder="Selecione a empresa"
          />
        </pulse-field>
        <pulse-field label="Filtrar por">
          <pulse-select v-model="selectMode" :items="['Data da Venda']" />
        </pulse-field>
        <pulse-field label="Data inicial">
          <pulse-date-picker
            v-model="selectedStartDateISO"
            @input="handleStartDateSelection"
          />
        </pulse-field>
        <pulse-field label="Data final">
          <pulse-date-picker
            v-model="selectedEndDateISO"
            @input="handleEndDateSelection"
          />
        </pulse-field>
        <div class="filtros-acoes">
          <pulse-button
            label="Limpar"
            icon="pi pi-times"
            severity="secondary"
            outlined
            @click="limparFiltro"
          />
          <pulse-button
            label="Filtrar"
            icon="pi pi-filter"
            :loading="isLoading"
            @click="applyRequiredFilters"
          />
        </div>
      </div>
    </pulse-card>

    <!-- Container 2: Indicadores -->
    <div class="metricas-grid">
      <pulse-summary-card
        v-for="metric in boardMetrics"
        :key="metric.key"
        :title="metric.label"
        :value="metric.value"
        :icon="metric.icon"
        :tone="metric.tone"
        :loading="isLoading"
      />
    </div>

    <!-- Container 3: Kanban / Lista -->
    <pulse-card title="Vendas" subtitle="Contratos organizados por etapa do fluxo.">
      <template #actions>
        <div class="pulse-segmented">
          <pulse-button
            label="Kanban"
            icon="pi pi-table"
            text
            :severity="boardViewMode === 'kanban' ? 'primary' : 'secondary'"
            @click="boardViewMode = 'kanban'"
          />
          <pulse-button
            label="Lista"
            icon="pi pi-list"
            text
            :severity="boardViewMode === 'list' ? 'primary' : 'secondary'"
            @click="boardViewMode = 'list'"
          />
        </div>
      </template>

      <div class="board-filtros">
        <pulse-field label="Busca" class="board-filtros__busca">
          <pulse-input-text
            v-model="filters.search"
            icon="pi pi-search"
            placeholder="Cliente ou contrato"
            clearable
          />
        </pulse-field>
        <pulse-field v-if="canViewAllSellers" label="Vendedor">
          <pulse-select
            v-model="filters.seller"
            :items="sellerOptions"
            item-text="label"
            item-value="value"
          />
        </pulse-field>
        <pulse-field v-if="contractTypeOptions.length" label="Tipo contrato">
          <pulse-select
            v-model="filters.contractType"
            :items="contractTypeOptions"
            item-text="label"
            item-value="value"
            placeholder="Todos"
            multiple
            chips
            small-chips
            deletable-chips
          />
        </pulse-field>
        <pulse-field label="Parcelas em atraso">
          <pulse-select
            v-model="filters.lateInstallments"
            :items="lateInstallmentOptions"
            item-text="label"
            item-value="value"
            placeholder="Todas"
            multiple
            chips
            small-chips
            deletable-chips
          />
        </pulse-field>
        <pulse-button
          icon="pi pi-filter-slash"
          severity="secondary"
          outlined
          title="Limpar filtros"
          @click="clearFilters"
        />
      </div>

      <div
        v-if="filterAlert && !hasAppliedRequiredFilters"
        class="pulse-message mt-4"
      >
        <i class="pi pi-info-circle" />
        {{ filterAlert }}
      </div>

      <div class="pt-4 pb-2">
        <fluxo-tipo-etapa-chips
          v-model="tiposEtapaFiltroSelecionados"
          :items="tiposEtapaFiltroItems"
        />
      </div>

      <div v-if="boardViewMode === 'list'" class="ticket-list-view mt-4">
        <v-data-table
          :headers="listHeaders"
          :items="listCards"
          :items-per-page="25"
          :loading="isLoading"
          class="pulse-table"
        >
          <template v-slot:item="row">
            <tr class="pointer" @click="selectCard(row.item)">
              <td class="etapa-lista-cell">
                <pulse-tag :value="row.item.stageName" :color="row.item.stageColor" />
              </td>
              <td class="cliente-lista-cell">
                <div class="lista-titulo">{{ row.item.customerName || "-" }}</div>
                <div class="lista-subtexto">
                  {{ row.item.document || "-" }}
                </div>
              </td>
              <td>{{ row.item.contractNumber || "-" }}</td>
              <td>{{ row.item.contractType || "-" }}</td>
              <td>{{ row.item.sellerName || "-" }}</td>
              <td>{{ formatDate(row.item.saleDate) }}</td>
              <td class="text-right">
                {{ row.item.totalInstallments }}
              </td>
              <td>
                <div class="late-chip-row">
                  <pulse-tag
                    v-for="numero in row.item.lateInstallments"
                    :key="`${row.item.id}-late-${numero}`"
                    severity="danger"
                    :value="getLateInstallmentLabel(numero)"
                  />
                  <span v-if="!row.item.lateInstallments.length">-</span>
                </div>
              </td>
              <td class="text-right">
                {{ formatCurrency(row.item.overdueAmount) }}
              </td>
            </tr>
          </template>
          <template #no-data>Nenhum contrato encontrado.</template>
        </v-data-table>
      </div>

      <div v-else class="kanban-board thin-scrollbar mt-4">
        <section
          v-for="column in visibleColumns"
          :key="column.id"
          class="kanban-column"
          :style="{ borderColor: column.color + '30', backgroundColor: column.color + '08' }"
        >
          <header
            class="column-header"
            :style="{
              background: `linear-gradient(135deg, ${column.color}15 0%, ${column.color}05 100%)`,
              borderBottom: `2px solid ${column.color}20`,
            }"
          >
            <div class="column-header__main">
              <div class="column-icon" :style="{ background: column.color }">
                <v-icon color="white" small>{{ column.icon }}</v-icon>
              </div>
              <div class="min-width-0">
                <h3 class="column-title">{{ column.title }}</h3>
                <p class="column-description">{{ column.description }}</p>
              </div>
              <div class="column-count" :style="{ background: column.color }">
                {{ getCardsByColumn(column.id).length }}
              </div>
            </div>
            <div class="column-total">
              <span>Total inadimplente</span>
              <strong>{{ formatCurrency(getColumnTotal(column.id)) }}</strong>
            </div>
          </header>

          <div class="card-list thin-scrollbar">
            <transition-group name="fade" type="transition" tag="div">
              <article
                v-for="card in getVisibleCardsByColumn(column.id)"
                :key="card.id"
                class="finance-card"
                @click="selectCard(card)"
              >
                <div class="card-code">{{ card.code }}</div>
                <h4 class="card-customer">{{ card.customerName }}</h4>
                <p class="card-description">{{ card.contractType }}</p>

                <div class="card-amount-row">
                  <div>
                    <span>Valor inadimplente</span>
                    <small>{{ getInstallmentsLabel(card) }}</small>
                  </div>
                  <strong>{{ formatCurrency(card.overdueAmount) }}</strong>
                </div>

                <div class="card-dates">
                  <span>
                    <i class="pi pi-shopping-cart" />
                    <strong>Venda:</strong>
                    {{ formatDate(card.saleDate) }}
                  </span>
                  <span>
                    <i class="pi pi-file" />
                    <strong>Contrato:</strong>
                    {{ formatDate(card.contractDate) }}
                  </span>
                  <span v-if="card.cancellationDate">
                    <i class="pi pi-ban" />
                    <strong>Cancelamento:</strong>
                    {{ formatDate(card.cancellationDate) }}
                  </span>
                  <span>
                    <i class="pi pi-user" />
                    {{ card.sellerName || "-" }}
                  </span>
                </div>

                <div v-if="card.lateInstallments.length" class="card-tags">
                  <pulse-tag
                    v-for="numero in card.lateInstallments"
                    :key="`${card.id}-late-${numero}`"
                    severity="danger"
                    icon="pi pi-exclamation-circle"
                    :value="getLateInstallmentLabel(numero)"
                  />
                </div>
              </article>

              <pulse-button
                v-if="hasMoreCards(column.id)"
                :key="`${column.id}-load-more`"
                :label="`Ver mais (${
                  getCardsByColumn(column.id).length -
                  (columnCardsLimit[column.id] || initialCardsPerColumn)
                } restantes)`"
                icon="pi pi-chevron-down"
                severity="secondary"
                text
                small
                block
                class="mt-1"
                @click="loadMoreCards(column.id)"
              />

              <div
                v-if="getCardsByColumn(column.id).length === 0"
                :key="`${column.id}-empty`"
                class="empty-column"
              >
                <i class="pi pi-inbox" />
                <span>Nenhum contrato nesta etapa</span>
              </div>
            </transition-group>
          </div>
        </section>
      </div>
    </pulse-card>

    <v-dialog v-model="detailDialog" max-width="920" scrollable persistent>
      <v-card v-if="selectedCard" class="pulse-dialog">
        <div class="pulse-dialog__header">
          <div class="min-width-0">
            <h2 class="dialog-customer">{{ selectedCard.customerName }}</h2>
            <div class="dialog-contract">
              <strong>{{ selectedCard.contractNumber }}</strong>
              <span>•</span>
              <span>{{ formatCpfMasked(selectedCard.document) }}</span>
              <span>•</span>
              <span>Venda #{{ selectedCard.id_venda }}</span>
            </div>
          </div>
          <div class="dialog-header-right">
            <div class="dialog-amount">
              <span>Valor inadimplente</span>
              <strong>{{ formatCurrency(selectedCard.overdueAmount) }}</strong>
            </div>
            <pulse-button
              icon="pi pi-times"
              severity="secondary"
              text
              rounded
              title="Fechar"
              @click="detailDialog = false"
            />
          </div>
        </div>

        <v-card-text class="pulse-dialog__body">
          <div class="dialog-tags">
            <pulse-tag :value="selectedCard.stageName" :color="selectedCard.stageColor" />
            <pulse-tag severity="secondary" :value="getInstallmentsLabel(selectedCard)" />
            <pulse-tag
              v-for="numero in selectedCard.lateInstallments"
              :key="`dialog-late-${numero}`"
              severity="danger"
              icon="pi pi-exclamation-circle"
              :value="getLateInstallmentLabel(numero)"
            />
          </div>

          <v-tabs v-model="detailTab" show-arrows class="pulse-tabs mt-4">
            <v-tab v-for="tab in detailTabs" :key="tab.key">
              {{ tab.label }}
            </v-tab>
          </v-tabs>

          <v-progress-linear
            v-if="loadingCardDetails"
            indeterminate
            color="#6366f1"
            height="2"
          ></v-progress-linear>

          <v-tabs-items v-model="detailTab" class="detail-tabs-content mt-0">
            <v-tab-item>
              <div class="detail-section">
                <div class="section-kicker">
                  <i class="pi pi-heart" />
                  Plano contratado
                </div>
                <h3 class="section-title">
                  {{ selectedCard.contractType || "Plano não informado" }}
                </h3>
                <div class="section-subtitle">
                  Data de adesão: {{ formatDate(selectedCard.contractDate) }}
                </div>

                <div class="data-grid">
                  <div
                    v-for="item in customerDataItems"
                    :key="item.key"
                    class="data-item"
                  >
                    <v-icon small>{{ item.icon }}</v-icon>
                    <span :title="item.value">{{ item.value }}</span>
                  </div>
                </div>

                <div class="section-kicker mt-6">
                  <i class="pi pi-shield" />
                  Sinistros
                </div>
                <div v-if="selectedCard.claims.length">
                  <div
                    v-for="claim in selectedCard.claims"
                    :key="claim.id"
                    class="record-row"
                  >
                    <div>
                      <strong>{{ claim.title }}</strong>
                      <span>{{ formatDate(claim.date) }}</span>
                    </div>
                    <pulse-tag severity="secondary" :value="claim.status" />
                  </div>
                </div>
                <div v-else class="empty-detail">
                  Nenhum sinistro registrado.
                </div>
              </div>
            </v-tab-item>

            <v-tab-item>
              <div class="detail-section">
                <div class="installment-filter">
                  <div class="installment-filter__title">
                    <span>Histórico das mensalidades</span>
                    <pulse-button
                      :label="`Vencimento ${installmentSortDesc ? '↓' : '↑'}`"
                      :icon="installmentSortDesc ? 'pi pi-sort-amount-down' : 'pi pi-sort-amount-up'"
                      :title="
                        installmentSortDesc
                          ? 'Vencimento: mais recente primeiro'
                          : 'Vencimento: mais antigo primeiro'
                      "
                      text
                      small
                      @click="toggleInstallmentSort"
                    />
                  </div>
                  <div class="installment-filter__options">
                    <pulse-button
                      v-for="filter in installmentFilters"
                      :key="filter.key"
                      :label="filter.label"
                      :severity="installmentFilter === filter.key ? 'primary' : 'secondary'"
                      rounded
                      small
                      @click="setInstallmentFilter(filter.key)"
                    />
                  </div>
                </div>

                <div v-if="filteredInstallments.length">
                  <div
                    v-for="installment in paginatedInstallments"
                    :key="installment.id"
                    class="installment-row"
                  >
                    <pulse-tag
                      class="installment-status"
                      :severity="
                        installmentSeverity[
                          getInstallmentStatusConfig(installment.status).color
                        ] || 'secondary'
                      "
                      :value="getInstallmentStatusConfig(installment.status).label"
                    />

                    <div class="installment-main">
                      <strong>
                        {{ installment.reference || "Sem identificação" }}
                      </strong>
                      <span>
                        #{{ installment.id }}
                        <template v-if="installment.type">
                          · {{ installment.type }}
                        </template>
                      </span>
                    </div>

                    <div class="installment-values">
                      <span>Valor da parcela</span>
                      <strong>{{ formatCurrency(installment.amount) }}</strong>
                      <template v-if="installment.paidAmount != null">
                        <span>Valor recebido</span>
                        <strong class="received-value">
                          {{ formatCurrency(installment.paidAmount) }}
                        </strong>
                      </template>
                    </div>

                    <div class="installment-dates">
                      <span>
                        <i class="pi pi-calendar" />
                        Vencimento: {{ formatDate(installment.dueDate) }}
                      </span>
                      <span v-if="installment.paidDate" class="installment-paid">
                        <i class="pi pi-check-circle" />
                        Pagamento: {{ formatDate(installment.paidDate) }}
                      </span>
                      <span v-else>
                        <i class="pi pi-minus-circle" />
                        Pagamento não realizado
                      </span>
                    </div>
                  </div>
                  <v-pagination
                    v-if="totalInstallmentsPages > 1"
                    v-model="installmentsPage"
                    :length="totalInstallmentsPages"
                    :total-visible="5"
                    class="pulse-paginator mt-4"
                  ></v-pagination>
                </div>
                <div v-else class="empty-detail">
                  Nenhuma parcela encontrada para este filtro.
                </div>
              </div>
            </v-tab-item>

            <v-tab-item>
              <div class="detail-section">
                <div v-if="paginatedAgreements.length">
                  <div
                    v-for="agreement in paginatedAgreements"
                    :key="agreement.id"
                    class="record-row"
                  >
                    <div>
                      <strong>{{ agreement.title }}</strong>
                      <span>
                        {{ formatDate(agreement.date) }}
                        -
                        {{ formatCurrency(agreement.amount) }}
                      </span>
                    </div>
                    <pulse-tag :value="agreement.status" />
                  </div>
                  <v-pagination
                    v-if="totalAgreementsPages > 1"
                    v-model="agreementsPage"
                    :length="totalAgreementsPages"
                    :total-visible="5"
                    class="pulse-paginator mt-4"
                  ></v-pagination>
                </div>
                <div v-else class="empty-detail">Nenhum acordo registrado.</div>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn icon small v-bind="attrs" @click="snackbar.show = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import fluxoVendasService, {
  ETAPAS_VENDAS,
} from "@/services/fluxoVendas.service";
import FluxoTipoEtapaChips from "./components/FluxoTipoEtapaChips.vue";
import { toCompanyOptions } from "@/utils/companyDisplay";

const EMPRESAS = [
  "A FLOR DO CAFÉ",
  "CEMITERIO PREVIDA",
  "CEMITERIO PREVIDA SÃO MIGUEL",
  "MEMORIAL PARQUE MACEIÓ",
  "PARQUE DAS FLORES",
  "PARQUE DO AGRESTE",
  "PREVIDA",
];

const INADIMPLENTES = [
  ETAPAS_VENDAS.INAD_1_PARCELA,
  ETAPAS_VENDAS.INAD_2_PARCELA,
  ETAPAS_VENDAS.INAD_3_PARCELA,
];

export default {
  name: "FluxoVendas",
  components: {
    FluxoTipoEtapaChips,
  },
  data() {
    return {
      isLoading: false,
      detailDialog: false,
      selectedCard: null,
      loadingCardDetails: false,
      detailTab: 0,
      installmentFilter: "todas",
      installmentsPage: 1,
      installmentsPerPage: 5,
      installmentSortDesc: false,
      agreementsPage: 1,
      agreementsPerPage: 10,
      detailTabs: [
        { key: "dados", label: "Dados" },
        { key: "parcelas", label: "Parcelas" },
        { key: "acordos", label: "Acordos" },
      ],
      installmentFilters: [
        { key: "todas", label: "Todas" },
        { key: "paga", label: "Pagas" },
        { key: "a_vencer", label: "A vencer" },
        { key: "vencida", label: "Vencidas" },
      ],
      installmentStatusMap: {
        paga: { label: "pago", color: "teal" },
        a_vencer: { label: "a vencer", color: "blue" },
        vencida: { label: "vencida", color: "red" },
      },
      installmentSeverity: {
        teal: "success",
        blue: "info",
        red: "danger",
      },
      columnCardsLimit: {},
      initialCardsPerColumn: 5,
      cardsIncrement: 5,
      hasAppliedRequiredFilters: false,
      filterAlert: "",
      selectedCompany: "",
      selectedStartDate: "",
      selectedEndDate: "",
      selectedStartDateISO: "",
      selectedEndDateISO: "",
      selectMode: "Data da Venda",
      boardViewMode: "kanban",
      mostrarFiltro: true,
      isStartDatePickerOpen: false,
      isEndDatePickerOpen: false,
      columns: [],
      tiposEtapaFiltroSelecionados: [],
      tiposEtapaFiltroInicializado: false,
      cards: [],
      companyOptions: [],
      filters: {
        search: "",
        seller: "todos",
        contractType: [],
        lateInstallments: [],
      },
      lateInstallmentOptions: [
        { value: 1, label: "1ª em atraso" },
        { value: 2, label: "2ª em atraso" },
        { value: 3, label: "3ª em atraso" },
      ],
      listHeaders: [
        { text: "Etapa", value: "stageName", width: "150" },
        { text: "Cliente", value: "customerName", width: "220" },
        { text: "Contrato", value: "contractNumber", width: "120" },
        { text: "Tipo", value: "contractType" },
        { text: "Vendedor", value: "sellerName", width: "180" },
        { text: "Venda", value: "saleDate", width: "100" },
        { text: "Mensalidades", value: "totalInstallments", align: "end" },
        { text: "Em atraso", value: "lateInstallments", sortable: false, width: "160" },
        { text: "Vencido", value: "overdueAmount", align: "end" },
      ],
      snackbar: {
        show: false,
        text: "",
        color: "info",
        timeout: 3000,
      },
    };
  },
  computed: {
    orderedColumns() {
      return [...this.columns].sort((a, b) => a.order - b.order);
    },
    canViewAllSellers() {
      const roles = this.getUserRoles();
      return (
        roles.includes("ROLE_ADMIN") ||
        roles.includes("ROLE_FLUXO_VENDAS_SUPERVISOR")
      );
    },
    filteredCards() {
      const search = (this.filters.search || "").toLowerCase();
      return this.cards.filter((card) => {
        const matchesSearch =
          !search ||
          this.getCardText(card.customerName).includes(search) ||
          this.getCardText(card.code).includes(search);
        const matchesSeller =
          !this.canViewAllSellers ||
          this.filters.seller === "todos" ||
          card.sellerCpf === this.filters.seller;
        const matchesContractType =
          !this.filters.contractType.length ||
          this.filters.contractType.includes(card.contractType);
        const matchesLateInstallments =
          !this.filters.lateInstallments.length ||
          this.filters.lateInstallments.some((numero) =>
            card.lateInstallments.includes(numero),
          );

        return (
          matchesSearch &&
          matchesSeller &&
          matchesContractType &&
          matchesLateInstallments
        );
      });
    },
    boardMetrics() {
      const countBy = (stages) =>
        this.filteredCards.filter((card) => stages.includes(card.stage))
          .length;

      return [
        {
          key: "total",
          label: "Total Contratos",
          value: this.filteredCards.length,
          icon: "pi pi-file",
          tone: "blue",
        },
        {
          key: "adimplentes",
          label: "Total Adimplentes",
          value: countBy([ETAPAS_VENDAS.ADIMPLENTES]),
          icon: "pi pi-check-circle",
          tone: "green",
        },
        {
          key: "inadimplentes",
          label: "Total Inadimplentes",
          value: countBy(INADIMPLENTES),
          icon: "pi pi-exclamation-circle",
          tone: "red",
        },
        {
          key: "cancelados",
          label: "Total Cancelados",
          value: countBy([ETAPAS_VENDAS.CANCELADO]),
          icon: "pi pi-ban",
          tone: "amber",
        },
      ];
    },
    tiposEtapaFiltroItems() {
      return this.getTiposEtapaDisponiveis().map((tipo) => ({
        label: tipo,
        color: this.getCorTipoEtapa(tipo),
        count: this.getQuantidadeColunasPorTipo(tipo),
      }));
    },
    visibleColumns() {
      return this.getColunasFiltradas();
    },
    listCards() {
      if (!this.getTiposEtapaFiltroAtivos().length) return this.filteredCards;
      const visibleIds = this.visibleColumns.map((column) => column.id);
      return this.filteredCards.filter((card) => visibleIds.includes(card.stage));
    },
    sellerOptions() {
      const sellers = new Map();
      this.cards.forEach((card) => {
        if (card.sellerCpf && !sellers.has(card.sellerCpf)) {
          sellers.set(card.sellerCpf, card.sellerName || card.sellerCpf);
        }
      });
      return [
        { value: "todos", label: "Todos" },
        ...[...sellers.entries()]
          .map(([value, label]) => ({ value, label }))
          .sort((a, b) => a.label.localeCompare(b.label, "pt-BR")),
      ];
    },
    contractTypeOptions() {
      return [
        ...new Set(this.cards.map((card) => card.contractType).filter(Boolean)),
      ]
        .sort((a, b) => a.localeCompare(b, "pt-BR", { numeric: true }))
        .map((type) => ({ value: type, label: type }));
    },
    customerDataItems() {
      const card = this.selectedCard;
      if (!card) return [];
      const items = [
        {
          key: "phone",
          icon: "mdi-phone-outline",
          value: card.phone || "Telefone não informado",
        },
        {
          key: "email",
          icon: "mdi-email-outline",
          value: card.email || "E-mail não informado",
        },
        {
          key: "sale",
          icon: "mdi-calendar-outline",
          value: `Data Venda: ${this.formatDate(card.saleDate)}`,
        },
        {
          key: "seller",
          icon: "mdi-account-outline",
          value: `Vendedor: ${card.sellerName || "-"}`,
        },
      ];
      if (card.cancellationDate) {
        items.push({
          key: "cancellation",
          icon: "mdi-cancel",
          value: `Cancelamento: ${this.formatDate(card.cancellationDate)}`,
        });
      }
      return items;
    },
    filteredInstallments() {
      if (!this.selectedCard) return [];
      const direction = this.installmentSortDesc ? -1 : 1;
      const dueTime = (installment) => {
        const time = new Date(installment.dueDate).getTime();
        return Number.isNaN(time) ? 0 : time;
      };
      return (this.selectedCard.installments || [])
        .filter(
          (installment) =>
            this.installmentFilter === "todas" ||
            installment.status === this.installmentFilter,
        )
        .sort((a, b) => (dueTime(a) - dueTime(b)) * direction);
    },
    paginatedInstallments() {
      const start = (this.installmentsPage - 1) * this.installmentsPerPage;
      return this.filteredInstallments.slice(
        start,
        start + this.installmentsPerPage,
      );
    },
    totalInstallmentsPages() {
      return Math.ceil(
        this.filteredInstallments.length / this.installmentsPerPage,
      );
    },
    paginatedAgreements() {
      const agreements = (this.selectedCard && this.selectedCard.agreements) || [];
      const start = (this.agreementsPage - 1) * this.agreementsPerPage;
      return agreements.slice(start, start + this.agreementsPerPage);
    },
    totalAgreementsPages() {
      const agreements = (this.selectedCard && this.selectedCard.agreements) || [];
      return Math.ceil(agreements.length / this.agreementsPerPage);
    },
  },
  async mounted() {
    this.setDefaultDateRange();
    this.loadCompanies();
    await this.loadColumns();
    if (this.companyOptions.length === 1) {
      this.applyRequiredFilters();
    } else {
      this.filterAlert =
        "Selecione uma empresa e um período para carregar o fluxo de vendas.";
    }
  },
  methods: {
    async loadColumns() {
      try {
        this.columns = await fluxoVendasService.getColumns();
        this.inicializarFiltroEtapasPadrao();
      } catch (error) {
        console.error("Erro ao carregar etapas do fluxo de vendas:", error);
        this.columns = [];
        this.showSnackbar("Erro ao carregar etapas do fluxo de vendas.", "error");
      }
    },
    inicializarFiltroEtapasPadrao() {
      const tiposValidos = this.getTiposEtapaDisponiveis();

      this.tiposEtapaFiltroSelecionados =
        this.tiposEtapaFiltroSelecionados.filter((tipo) =>
          tiposValidos.includes(tipo),
        );

      if (this.tiposEtapaFiltroInicializado) return;

      this.tiposEtapaFiltroSelecionados = [...tiposValidos];
      this.tiposEtapaFiltroInicializado = true;
    },
    getTiposEtapaDisponiveis() {
      return [
        ...new Set(this.columns.map((coluna) => coluna.tipo).filter(Boolean)),
      ];
    },
    getTiposEtapaFiltroAtivos() {
      return Array.isArray(this.tiposEtapaFiltroSelecionados)
        ? this.tiposEtapaFiltroSelecionados
        : [];
    },
    getQuantidadeColunasPorTipo(tipo) {
      return this.columns.filter((coluna) => coluna.tipo === tipo).length;
    },
    getCorTipoEtapa(tipo) {
      const coluna = this.columns.find((item) => item.tipo === tipo);
      return coluna?.cor || "#607d8b";
    },
    getColunasFiltradas() {
      const tiposAtivos = this.getTiposEtapaFiltroAtivos();
      if (!tiposAtivos.length) return this.orderedColumns;

      return this.orderedColumns.filter((coluna) =>
        tiposAtivos.includes(coluna.tipo),
      );
    },
    async applyRequiredFilters() {
      if (!this.canLoadCards()) return;

      this.isLoading = true;
      try {
        if (!this.columns.length) await this.loadColumns();
        const contratos = await fluxoVendasService.getContratos({
          dataInicio: this.selectedStartDateISO,
          dataFim: this.selectedEndDateISO,
          empresa: this.selectedCompany,
        });
        this.cards = contratos.map((card) => {
          const column = this.columns.find((item) => item.id === card.stage);
          return {
            ...card,
            stageName: column ? column.title : "-",
            stageColor: column ? column.color : "#64748b",
          };
        });
        if (
          this.filters.seller !== "todos" &&
          !this.cards.some((card) => card.sellerCpf === this.filters.seller)
        ) {
          this.filters.seller = "todos";
        }
        this.initializeColumnLimits();
        this.hasAppliedRequiredFilters = true;
        this.filterAlert = "";
      } catch (error) {
        console.error("Erro ao carregar fluxo de vendas:", error);
        this.cards = [];
        this.showSnackbar(
          error.response?.data?.message || "Erro ao carregar fluxo de vendas.",
          "error",
        );
      } finally {
        this.isLoading = false;
      }
    },
    loadCompanies() {
      const empresas = [...EMPRESAS];
      this.companyOptions = toCompanyOptions(empresas);
      if (this.companyOptions.length === 1) {
        this.selectedCompany = this.companyOptions[0].value;
      } else if (
        this.selectedCompany &&
        !this.companyOptions.some(
          (company) => company.value === this.selectedCompany
        )
      ) {
        this.selectedCompany = "";
      }
    },
    canLoadCards() {
      if (
        !this.selectedCompany ||
        !this.selectedStartDateISO ||
        !this.selectedEndDateISO
      ) {
        this.hasAppliedRequiredFilters = false;
        this.cards = [];
        this.filterAlert =
          "Informe empresa, data inicial e data final antes de carregar os dados.";
        return false;
      }
      if (new Date(this.selectedStartDateISO) > new Date(this.selectedEndDateISO)) {
        this.hasAppliedRequiredFilters = false;
        this.cards = [];
        this.filterAlert = "A data inicial não pode ser maior que a data final.";
        return false;
      }
      return true;
    },
    getCardsByColumn(columnId) {
      return this.filteredCards.filter((card) => card.stage === columnId);
    },
    getVisibleCardsByColumn(columnId) {
      const limit = this.columnCardsLimit[columnId] || this.initialCardsPerColumn;
      return this.getCardsByColumn(columnId).slice(0, limit);
    },
    hasMoreCards(columnId) {
      const limit = this.columnCardsLimit[columnId] || this.initialCardsPerColumn;
      return this.getCardsByColumn(columnId).length > limit;
    },
    loadMoreCards(columnId) {
      const currentLimit =
        this.columnCardsLimit[columnId] || this.initialCardsPerColumn;
      this.$set(this.columnCardsLimit, columnId, currentLimit + this.cardsIncrement);
    },
    initializeColumnLimits() {
      this.columnCardsLimit = {};
      this.columns.forEach((column) => {
        this.$set(this.columnCardsLimit, column.id, this.initialCardsPerColumn);
      });
    },
    getColumnTotal(columnId) {
      return this.getCardsByColumn(columnId).reduce(
        (total, card) => total + card.overdueAmount,
        0,
      );
    },
    getInstallmentsLabel(card) {
      if (!card.totalInstallments) return "Sem mensalidades lançadas";
      const vencidas = card.overdueInstallments;
      const vencidasLabel = !vencidas
        ? "3 primeiras em dia"
        : vencidas === 1
        ? "1 vencida nas 3 primeiras"
        : `${vencidas} vencidas nas 3 primeiras`;
      return `${card.totalInstallments} lançada(s) • ${vencidasLabel}`;
    },
    getLateInstallmentLabel(numero) {
      return `${numero}ª em atraso`;
    },
    async selectCard(card) {
      this.selectedCard = {
        ...card,
        installments: [],
        agreements: [],
        claims: [],
      };
      this.detailTab = 0;
      this.installmentFilter = "todas";
      this.installmentsPage = 1;
      this.installmentSortDesc = false;
      this.agreementsPage = 1;
      this.detailDialog = true;
      await this.loadCardDetails(card);
    },
    async loadCardDetails(card) {
      if (!card.id_contrato) return;

      this.loadingCardDetails = true;
      try {
        const [installments, agreements, claims] = await Promise.all([
          fluxoVendasService.getInstallments(card.id_contrato),
          fluxoVendasService.getAgreements(card.id_contrato),
          fluxoVendasService.getClaims(card.id_contrato),
        ]);
        if (this.selectedCard && this.selectedCard.id === card.id) {
          this.selectedCard = {
            ...this.selectedCard,
            installments,
            agreements,
            claims,
          };
        }
      } catch (error) {
        console.error("Erro ao carregar detalhes do contrato:", error);
        this.showSnackbar("Erro ao carregar detalhes do contrato.", "error");
      } finally {
        this.loadingCardDetails = false;
      }
    },
    setInstallmentFilter(key) {
      this.installmentFilter = key;
      this.installmentsPage = 1;
    },
    toggleInstallmentSort() {
      this.installmentSortDesc = !this.installmentSortDesc;
      this.installmentsPage = 1;
    },
    getInstallmentStatusConfig(status) {
      return (
        this.installmentStatusMap[status] || {
          label: status || "sem status",
          color: "grey",
        }
      );
    },
    clearFilters() {
      this.filters = {
        search: "",
        seller: "todos",
        contractType: [],
        lateInstallments: [],
      };
    },
    alternarVisibilidade() {
      this.mostrarFiltro = !this.mostrarFiltro;
    },
    limparFiltro() {
      this.selectedCompany = this.companyOptions.length === 1 ? this.selectedCompany : "";
      this.setDefaultDateRange();
      this.hasAppliedRequiredFilters = false;
      this.cards = [];
      this.clearFilters();
    },
    setDefaultDateRange() {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth() - 3, 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
      this.selectedStartDateISO = this.formatDateInput(firstDay);
      this.selectedEndDateISO = this.formatDateInput(lastDay);
      this.selectedStartDate = this.convertToBR(this.selectedStartDateISO);
      this.selectedEndDate = this.convertToBR(this.selectedEndDateISO);
    },
    handleStartDateSelection() {
      this.selectedStartDate = this.convertToBR(this.selectedStartDateISO);
      this.isStartDatePickerOpen = false;
    },
    handleEndDateSelection() {
      this.selectedEndDate = this.convertToBR(this.selectedEndDateISO);
      this.isEndDatePickerOpen = false;
    },
    formatDateInput(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    convertToBR(dateString) {
      if (!dateString) return "";
      const [year, month, day] = String(dateString).substring(0, 10).split("-");
      return `${day}/${month}/${year}`;
    },
    formatDate(value) {
      if (!value) return "-";
      return this.convertToBR(value);
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    formatCpfMasked(value) {
      if (!value) return "Documento não informado";
      const cpf = String(value).replace(/\D/g, "");
      if (cpf.length !== 11) return value;
      return `${cpf.substring(0, 3)}.***.**${cpf.substring(9, 11)}`;
    },
    getCardText(value) {
      return String(value || "").toLowerCase();
    },
    getUserRoles() {
      const user = JSON.parse(sessionStorage.getItem("user") || "{}");
      return Array.isArray(user.roles) ? user.roles : [];
    },
    showSnackbar(text, color = "info", timeout = 3000) {
      this.snackbar = { show: true, text, color, timeout };
    },
  },
};
</script>

<style scoped>
.fluxo-vendas-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 100%;
  padding: 16px;
  background-color: var(--app-bg);
}

@media (min-width: 640px) {
  .fluxo-vendas-page {
    padding: 24px;
  }
}

.min-width-0 {
  min-width: 0;
}

.pointer {
  cursor: pointer;
}

/* ── Filtros ── */
.filtros-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  align-items: end;
}

@media (min-width: 640px) {
  .filtros-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1264px) {
  .filtros-grid {
    grid-template-columns: minmax(220px, 1.4fr) minmax(160px, 1fr) 170px 170px auto;
  }
}

.filtros-acoes {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

/* ── Indicadores ── */
.metricas-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

@media (min-width: 640px) {
  .metricas-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1264px) {
  .metricas-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

/* ── Filtros do quadro ── */
.board-filtros {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  align-items: end;
}

@media (min-width: 960px) {
  .board-filtros {
    grid-template-columns: minmax(220px, 1.4fr) repeat(auto-fit, minmax(170px, 1fr)) 40px;
  }
}

/* ── Lista ── */
.lista-titulo {
  font-weight: 500;
  color: #334155;
}

.lista-subtexto {
  margin-top: 2px;
  font-size: 0.75rem;
  color: #94a3b8;
}

.etapa-lista-cell {
  width: 150px;
}

.cliente-lista-cell {
  width: 220px;
}

.late-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

/* ── Kanban ── */
.kanban-board {
  display: flex;
  gap: 16px;
  margin-left: -16px;
  margin-right: -16px;
  padding: 0 16px 16px;
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}

@media (min-width: 640px) {
  .kanban-board {
    margin-left: -24px;
    margin-right: -24px;
    padding-left: 24px;
    padding-right: 24px;
  }
}

.kanban-column {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 320px;
  min-height: 32rem;
  border: 2px solid;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
  transition: box-shadow 0.15s;
}

.kanban-column:hover {
  box-shadow: 0 4px 6px -1px rgba(15, 23, 42, 0.1);
}

.column-header {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 10px 10px 0 0;
}

.column-header__main {
  display: flex;
  align-items: center;
  gap: 10px;
}

.column-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.1);
}

.column-header__main .min-width-0 {
  flex: 1;
}

.column-title {
  margin: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 0.875rem;
  font-weight: 700;
  color: #1e293b;
}

.column-description {
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.3;
  color: #64748b;
}

.column-count {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: 28px;
  height: 28px;
  padding: 0 6px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.1);
}

.column-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.75rem;
  color: #64748b;
}

.column-total strong {
  font-size: 0.875rem;
  color: #1e293b;
}

.card-list {
  flex: 1;
  max-height: calc(100vh - 20rem);
  min-height: 26rem;
  padding: 12px;
  overflow-y: auto;
}

.card-list > div {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.finance-card {
  padding: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
  cursor: pointer;
  transition: box-shadow 0.15s, transform 0.15s;
}

.finance-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(15, 23, 42, 0.1);
}

.card-code {
  font-size: 0.6875rem;
  font-weight: 600;
  color: #94a3b8;
}

.card-customer {
  margin: 2px 0 0;
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.25;
  color: #1e293b;
}

.card-description {
  margin: 6px 0 10px;
  font-size: 0.75rem;
  line-height: 1.35;
  color: #64748b;
}

.card-amount-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 0.75rem;
  color: #64748b;
  background: #f8fafc;
}

.card-amount-row > div {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.card-amount-row small {
  margin-top: 2px;
  font-size: 0.6875rem;
  color: #94a3b8;
}

.card-amount-row strong {
  font-size: 0.9375rem;
  white-space: nowrap;
  color: #1e293b;
}

.card-dates {
  display: grid;
  gap: 6px;
  margin-top: 10px;
  font-size: 0.75rem;
  color: #475569;
}

.card-dates span {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.card-dates .pi {
  font-size: 0.7rem;
  color: #94a3b8;
}

.card-dates strong {
  font-weight: 600;
  color: #334155;
}

.card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 10px;
}

.empty-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 120px;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  font-size: 0.8125rem;
  color: #94a3b8;
  background: rgba(255, 255, 255, 0.6);
}

.empty-column .pi {
  font-size: 1.25rem;
}

/* ── Dialog ── */
.dialog-customer {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.25;
  color: #1e293b;
}

.dialog-contract {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
  font-size: 0.8125rem;
  color: #64748b;
}

.dialog-header-right {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.dialog-amount {
  min-width: 148px;
  text-align: right;
}

.dialog-amount span {
  display: block;
  font-size: 0.75rem;
  color: #64748b;
}

.dialog-amount strong {
  display: block;
  font-size: 1.375rem;
  line-height: 1.2;
  color: #1e293b;
}

.dialog-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 16px;
}

.detail-tabs-content {
  background: transparent !important;
}

.detail-section {
  padding-top: 16px;
}

.section-kicker {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #64748b;
}

.section-title {
  margin: 8px 0 4px;
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
}

.section-subtitle {
  margin: 2px 0 14px;
  font-size: 0.875rem;
  color: #94a3b8;
}

.data-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 26px;
}

.data-item {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 0.875rem;
  color: #334155;
}

.data-item span {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.record-row,
.installment-row {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
}

.record-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
  padding: 10px 12px;
}

.record-row strong,
.record-row span {
  display: block;
}

.record-row strong {
  font-size: 0.875rem;
  color: #334155;
}

.record-row span {
  font-size: 0.75rem;
  color: #64748b;
}

.installment-filter {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  font-size: 0.8125rem;
  color: #64748b;
}

.installment-filter__title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.installment-filter__options {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.installment-row {
  display: grid;
  grid-template-columns: auto minmax(130px, 0.8fr) minmax(150px, 1fr) minmax(210px, 1.3fr);
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
  padding: 10px 14px;
}

.installment-status {
  justify-content: center;
  min-width: 72px;
  text-transform: uppercase;
}

.installment-main,
.installment-values,
.installment-dates {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.installment-main strong,
.installment-values strong {
  font-size: 0.875rem;
  color: #334155;
}

.installment-main span,
.installment-values span,
.installment-dates {
  font-size: 0.75rem;
  color: #64748b;
}

.installment-values {
  grid-template-columns: auto auto;
  column-gap: 10px;
}

.installment-values strong {
  text-align: right;
}

.installment-values .received-value {
  color: #059669;
}

.installment-dates span {
  display: flex;
  align-items: center;
  gap: 5px;
}

.installment-dates .pi {
  font-size: 0.7rem;
}

.installment-paid .pi {
  color: #059669;
}

.empty-detail {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 80px;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #94a3b8;
}

@media (max-width: 767px) {
  .data-grid {
    grid-template-columns: 1fr;
  }

  .installment-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .dialog-amount {
    min-width: 0;
  }
}
</style>
